/* eslint-disable @typescript-eslint/naming-convention */

import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { delay, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { GenericModalService, ModalSettingsTemplate } from 'src/app/shared/generic-modal/generic-modal.service';
import { LoadingPanelService } from 'src/app/shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { DataFieldGroup, DataFieldTypes } from 'src/app/shared/datafields/models';
import { DataFieldGroupContainerComponent } from '../../../shared/datafields/datafield-group-container.component';
import { InitConfigurationDialogComponent } from '../../../distribution/init-configuration-dialog/init-configuration-dialog.component';
import { InitConfiguration } from '../../../distribution/models/init-configuration';
import { InitializeService } from '../../../distribution/services/initialize.service';
import { TransportInConfiguration, TransportInConfigurationAndValues } from '../../models/transport-configuration';
import { TransportType } from '../../models/transport-type';
import { TransportService } from '../../services/transport.service';
import { MonitorItem, MonitorItemClass } from 'src/app/monitor/models/monitor-item';
import { MonitorService } from 'src/app/monitor/services/monitor.service';
import { MonitorGroup } from 'src/app/monitor/models/monitor-group';
import { MonitorSchedule } from 'src/app/monitor/models/monitor-schedule';
import { Stakeholder } from 'src/app/stakeholder/models/stakeholder';
import { StakeholderService } from 'src/app/stakeholder/services/stakeholder.service';
import { MonitorItemEditComponent } from 'src/app/monitor/components/monitor-item-edit/monitor-item-edit.component';
import { ArtifactAssemblyTypes } from 'src/app/developer/models/artifact';
import { SecretService } from 'src/app/shared/secret/services/secret.service';
import { SvgIconComponent } from '../../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { AsyncPipe } from '@angular/common';
import { AutoFocusDirective } from '../../../shared/auto-focus/auto-focus.directive';
import { DurationPickerComponent } from '../../../shared/duration-picker/duration-picker.component';
import { ScheduleItemComponent } from '../../../shared/scheduling/schedule-item.component';
import { MonitorItemEditComponent as MonitorItemEditComponent_1 } from '../../../monitor/components/monitor-item-edit/monitor-item-edit.component';
import { TrackingSharedActionMethodsService } from 'src/app/tracking/services/tracking-shared-action-methods.service';
import { DxValidationGroupComponent, DxValidationGroupModule } from 'devextreme-angular';

export enum Mode { View, Edit, New }
const MaxNumberOfLocationsToShow = 10;

const nameValidator = (transportService: TransportService, ignoreId: () => number): AsyncValidatorFn =>
    async (control: AbstractControl) =>
        await transportService.isTransportInConfigurationNameAvailable(control.value as string, ignoreId()) ? null : { nameExists: true } as ValidationErrors;

@Component({
    selector: 'ep-transport-locations-details',
    templateUrl: './transport-locations-details.component.html',
    styleUrls: ['./transport-locations-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, FormsModule, ReactiveFormsModule, AutoFocusDirective, DurationPickerComponent, ScheduleItemComponent, DataFieldGroupContainerComponent, MonitorItemEditComponent_1, AsyncPipe, DxValidationGroupModule]
})
export class TransportLocationsDetailsComponent implements OnInit {
    @ViewChild('dfgc') datafieldGroupContainer: DataFieldGroupContainerComponent;
    @ViewChild(MonitorItemEditComponent) private monitorItemEdit: MonitorItemEditComponent = null;
    @ViewChild(DxValidationGroupComponent) validationGroup: DxValidationGroupComponent;
    @ViewChild('transportSchedule') private transportSchedule?: ScheduleItemComponent;
    
    mode = Mode.View;
    transportConfigurationsIds: number[] = [];
    duplicatetransportConfigurationsId = 0;
    transportConfigurations: TransportInConfiguration[] = [];
    selectedTransportConfiguration: TransportInConfiguration = null;
    transportTypes: TransportType[] = [];
    formModel: UntypedFormGroup;
    dataFieldGroups: DataFieldGroup[] = [];
    dataFieldsFormGroup = new UntypedFormGroup({});
    monitorGroups: MonitorGroup[] = [];
    stakeholders: Stakeholder[] = [];
    monitorSchedules: MonitorSchedule[] = [];
    isLoading = false;

    currentTransportTypeHasEmbeddedMonitor = new BehaviorSubject(false);
    currentTransportTypeHasEmbeddedMonitor$ = this.currentTransportTypeHasEmbeddedMonitor.pipe(
        distinctUntilChanged()
    );

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private fb: UntypedFormBuilder,
        private modalService: GenericModalService,
        private loadingPanelService: LoadingPanelService,
        private toastService: ToastService,
        private transportService: TransportService,
        private initializeService: InitializeService,
        private monitorService: MonitorService,
        private stakeholderService: StakeholderService,
        private secretService: SecretService,
        private trackingService: TrackingSharedActionMethodsService
    ) { }

    async ngOnInit() {
        // *** To avoid problems, keep this first
        this.formModel = this.buildFormModel();
        this.formModel.disable();

        this.route.params.pipe(
            map(e => this.convertToNumbers(e.transportConfigurationsIds)),
        ).subscribe(ids => {
            this.transportConfigurationsIds = ids.length < 1 ? [0] : ids.sort((a, b) => a - b);
            this.refresh();
        });
        this.route.paramMap.subscribe(params => {
            const duplicatetransportConfigurationsId = +params.get('duplicatetransportConfigurationsId');
            if (duplicatetransportConfigurationsId !== null && duplicatetransportConfigurationsId > 0) {
                this.duplicatetransportConfigurationsId = duplicatetransportConfigurationsId;
            }
        });

        this.fcTransportTypeId.valueChanges.pipe(
            distinctUntilChanged()  // Ignore changes where the actual valueis unchange, i.e. when enabled or disabled
        ).subscribe(async () => {
            if (!this.isLoading) {
                // Remove monitor when transport type is actually changed and it's not because we're loading (assuming it's user activity)
                this.removeMonitor();
            }
            await this.refreshForTransportConfiguration();
            this.updateTransportTypeHasEmbeddedMonitor();
        });
        await this.refreshForTransportConfiguration();

        this.formModel.statusChanges.pipe(
            distinctUntilChanged(),
            filter(e => e === 'INVALID'),
            delay(0) // Delay 0 ms to make sure the form is updated before we check the datafieldcontainer
        ).subscribe(() => {
            // Whenever the form becomes invalid; give the datafieldcontainer a chance to hide 'dependson' fields.
            this.datafieldGroupContainer.checkDependsOnFieldsInitial();
        });
    }

    convertToNumbers(input: string): number[] {
        const res = input.split(',').map(e => Number(e)).filter(i => i > 0).slice(0, MaxNumberOfLocationsToShow);
        return res;
    }

    canDeactivate(): Promise<boolean> | boolean {
        return this.InViewMode ? true : this.askCanDeactivate();
    }

    async askCanDeactivate(): Promise<boolean> {
        try {
            await this.modalService.confirmDirect('Not saved', 'You have not saved your changes. Continue?');
            return true;
        }
        catch {
            return false;
        }
    }

    async refresh(): Promise<void> {
        try {
            this.isLoading = true;
            this.loadingPanelService.showLoadingPanelDirect('Loading...');

            [
                this.transportTypes,
                this.monitorGroups,
                this.monitorSchedules,
                this.stakeholders
            ] = await Promise.all([
                this.transportService.getTransportTypes(this.transportService.transportTypesIn),
                this.monitorService.getMonitorGroups(),
                this.monitorService.getMonitorSchedules(),
                this.stakeholderService.getStakeholders()
            ]);

            if (this.transportConfigurationsIds.includes(0) && this.duplicatetransportConfigurationsId === 0) {
                await this.refreshNewTransportConfiguration();
            }
            else if (this.duplicatetransportConfigurationsId > 0) {
                await this.refreshDuplicateTransportConfiguration();
            }
            else {
                this.mode = Mode.View;
                this.transportConfigurations = (await this.transportService.getTransportInConfigurations(this.transportConfigurationsIds));
                this.formModel.disable();
            }

            if (this.selectedTransportConfiguration) {
                await this.select(this.selectedTransportConfiguration);
            }
            else {
                await this.select();
            }
        }
        catch (err) {
            console.error(err);
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
            this.isLoading = false;
        }
    }

    async refreshNewTransportConfiguration(): Promise<void> {
        this.mode = Mode.New;
        const newDate = new Date();
        newDate.setHours(0, 0, 0, 0);
        this.transportConfigurations = [new TransportInConfiguration()];
        this.transportConfigurations[0].Schedule.StartDate = newDate;
        this.transportConfigurations[0].TransportTypeId = null;
        this.formModel.enable();
    }

    async refreshDuplicateTransportConfiguration(): Promise<void> {
        this.mode = Mode.New;
        this.transportConfigurations = [new TransportInConfiguration()];
        const baseTransportConfig = (await this.transportService.getTransportInConfigurations([this.duplicatetransportConfigurationsId]));
        baseTransportConfig[0].MonitorItem = null;
        this.transportConfigurations[0] = baseTransportConfig[0];
        await this.select(this.transportConfigurations[0]);
        this.transportConfigurations[0].Name = await this.getAvailableNameForDuplicate(this.transportConfigurations[0].Name);
        this.transportConfigurations[0].Schedule.Enabled = false;
        this.transportConfigurations[0].Id = null;
        this.transportConfigurations[0].InitConfiguration.Id = null;
        this.transportConfigurations[0].Schedule.Id = null;
        this.transportConfigurations[0].Schedule.Triggers?.forEach(trigger => {
            trigger.Id = null;
        });
        this.selectedTransportConfiguration = this.transportConfigurations[0];
        this.formModel.enable();
    }

    async refreshForTransportConfiguration(): Promise<void> {
        const transportTypeId = +this.fcTransportTypeId.value;
        if (this.selectedTransportConfiguration && !isNaN(transportTypeId) && transportTypeId > 0) {
            this.dataFieldGroups = await this.transportService.getTransportDisplayGroups(transportTypeId, this.selectedTransportConfiguration.Id);
            if (this.duplicatetransportConfigurationsId > 0) {
                this.dataFieldGroups?.forEach(dfg => {
                    dfg.DataFields?.forEach(df => {
                        if (df.DataType.DataTypeEnum === DataFieldTypes.Password) {
                            df.SecretId = null;
                            df.Value = null;
                        }
                    });
                });
            }
            if (this.selectedTransportConfiguration.InitConfiguration?.Id) {

                // REMARK! The below is done because the transport-location and init-config are always stored together in a single call.
                // But if the user edits the transport-location without editing the init-config - the init-config won't contain the DataFieldValue fields.
                // So to simulate that the init-config dialog has been opened, we load the configuration for each disassemble-step in the init-config.
                // Then the values will be there on an update only on the transport-location.

                if (this.selectedTransportConfiguration.InitConfiguration.PreDisassembleInitStepId) {
                    const groups = await this.initializeService.getInitConfigInitStepDisplayGroups(
                        this.selectedTransportConfiguration.InitConfiguration.PreDisassembleInitStepId, this.selectedTransportConfiguration.InitConfiguration.PreDisassembleInitStepConfig);
                    this.selectedTransportConfiguration.InitConfiguration.PreDisassembleInitStepValues = DataFieldGroup.getDataFieldValues(groups, true);
                }

                if (this.selectedTransportConfiguration.InitConfiguration.CustomDisassembleInitStepId) {
                    const groups = await this.initializeService.getInitConfigInitStepDisplayGroups(
                        this.selectedTransportConfiguration.InitConfiguration.CustomDisassembleInitStepId, this.selectedTransportConfiguration.InitConfiguration.CustomDisassembleInitStepConfig);
                    this.selectedTransportConfiguration.InitConfiguration.CustomDisassembleInitStepValues = DataFieldGroup.getDataFieldValues(groups, true);
                }

                if (this.selectedTransportConfiguration.InitConfiguration.PostDisassembleInitStepId) {
                    const groups = await this.initializeService.getInitConfigInitStepDisplayGroups(
                        this.selectedTransportConfiguration.InitConfiguration.PostDisassembleInitStepId, this.selectedTransportConfiguration.InitConfiguration.PostDisassembleInitStepConfig);
                    this.selectedTransportConfiguration.InitConfiguration.PostDisassembleInitStepValues = DataFieldGroup.getDataFieldValues(groups, true);
                }
            }
        }
        else {
            this.dataFieldGroups = [];
        }
    }

    async select(transportConfiguration?: TransportInConfiguration): Promise<void> {
        try {
            this.loadingPanelService.showLoadingPanelDirect('Loading...');

            let toSelect: TransportInConfiguration = null;

            if (this.transportConfigurations.length > 0) {

                if (transportConfiguration) {
                    toSelect = this.transportConfigurations.find(e => e.Id === transportConfiguration.Id) ?? toSelect;
                }

                toSelect = toSelect ?? this.transportConfigurations[0];

                this.selectedTransportConfiguration = toSelect;
                this.updateTransportTypeHasEmbeddedMonitor();

                this.updateFormModel();
                if (this.selectedTransportConfiguration.Id) {
                    await this.refreshForTransportConfiguration();
                }
            }
        }
        catch (err) {
            console.error(err);
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    remove(transportConfigurationId: number): void {
        const f = this.transportConfigurationsIds.filter(e => e !== transportConfigurationId);
        if (f.length > 0) {
            this.navigateToDetails(f);
        }
        else {
            this.navigateToList();
        }
    }

    onEdit(): void {
        this.mode = Mode.Edit;
        this.formModel.enable();
        this.datafieldGroupContainer.edit();
    }

    onCancel(): void {
        this.transportSchedule?.clearValidationErrors();
        if (this.InNewMode) {
            this.navigateToList();
        }
        else {
            this.mode = Mode.View;
            this.formModel.disable();
            this.refresh();
            this.datafieldGroupContainer.cancel();
        }
    }

    canSave(): boolean {
        this.datafieldGroupContainer.commit();
        this.formModel.updateValueAndValidity();
        this.dataFieldsFormGroup.updateValueAndValidity();

        const validation = this.validationGroup?.instance?.validate();
        const transportScheduleValid = this.transportSchedule?.isScheduleValid ?? true;
        if (!this.formModel.valid || !this.dataFieldsFormGroup.valid || (validation && !validation.isValid) || !transportScheduleValid) {
            return false;
        }
        return true;
    }

    async onSave(): Promise<void> {        
        this.datafieldGroupContainer.commit();
        this.formModel.updateValueAndValidity();
        this.dataFieldsFormGroup.updateValueAndValidity();


        if (this.formModel.valid) {
            this.selectedTransportConfiguration.Name = this.fcName.value;
            this.selectedTransportConfiguration.Schedule.Enabled = this.fcEnabled.value;
            this.selectedTransportConfiguration.FrequencyInSeconds = +this.fcFrequency.value;
            this.selectedTransportConfiguration.TransportTypeId = +this.fcTransportTypeId.value;
            this.selectedTransportConfiguration.MonitorItem = this.selectedTransportConfigurationHasMonitor ? this.fcMonitorItem.value : null;

            if (await this.isNameAvailable(this.selectedTransportConfiguration.Name, this.selectedTransportConfiguration.Id)) {
                try {
                    this.loadingPanelService.showLoadingPanelDirect('Saving...');

                    const transportInConfigurationAndValues: TransportInConfigurationAndValues = {
                        DataFieldValues: DataFieldGroup.getDataFieldValues(this.dataFieldGroups, true),
                        TransportConfiguration: this.selectedTransportConfiguration,
                        MonitorConfigValues: this.selectedTransportConfigurationHasMonitor ? DataFieldGroup.getDataFieldValues(this.monitorItemEdit.artifactConfigDataFieldGroups, false) : null,
                    };

                    this.selectedTransportConfiguration = await this.transportService.saveTransportInConfiguration(transportInConfigurationAndValues);
                    if (this.duplicatetransportConfigurationsId > 0) {
                        this.mode = Mode.View;
                        this.navigateToList();
                    }
                    this.updateTransportTypeHasEmbeddedMonitor();
                    await this.refreshForTransportConfiguration(); // Make sure init-config disassembler values are loaded

                    if (this.InNewMode && this.duplicatetransportConfigurationsId === 0) {
                        this.mode = Mode.View;
                        this.navigateToDetails([this.selectedTransportConfiguration.Id]);
                    }
                    else {
                        this.mode = Mode.View;
                        this.formModel.disable();
                    }
                }
                catch (err) {
                    console.error(err);
                }
                finally {
                    this.loadingPanelService.hideLoadingPanel();
                }
            }
            else {
                await this.modalService.messageDirect('Name not available', 'The chosen name is not available. Please choose another one.');
            }
        }
    }

    onDelete(): void {
        this.modalService.confirmDirect('Delete transport location', 'Are you sure you want to delete the selected transport location?').then(() => {
            if (this.selectedTransportConfiguration) {
                this.transportService.deleteTransportInConfiguration(this.selectedTransportConfiguration.Id).then(() => {
                    this.toastService.showToast('Transport location deleted...', ToastType.success);
                    this.remove(this.selectedTransportConfiguration.Id);
                }).catch((err) => {
                    console.error(err);
                    this.toastService.showToast('Failed to delete transport locations...', ToastType.error);
                });
            }
        }).catch(() => { });
    }

    onTriggerNow(): void {
        this.modalService.confirmDirect('Trigger transport location', 'Are you sure you will trigger this transport location to start immediately?').then(() => {
            if (this.selectedTransportConfiguration) {

                this.transportService.setTriggerNowForInConfiguration(this.selectedTransportConfiguration.Id).then(() => {
                    this.toastService.showToast('Transport location triggered...', ToastType.success);
                }).catch((err) => {
                    console.error(err);
                    this.toastService.showToast('Failed to to trigger transport location...', ToastType.error);
                });
            }
        }).catch(() => { });
    }

    async onShowInitConfig(): Promise<void> {
        try {
            if (!this.selectedTransportConfiguration.InitConfiguration) {
                this.selectedTransportConfiguration.InitConfiguration = {} as InitConfiguration;
            }

            const componentData = {
                initConfiguration: this.selectedTransportConfiguration.InitConfiguration,
                headline: this.selectedTransportConfiguration.Name,
                disabled: this.InViewMode
            };
            const initConfigResult = await this.modalService.openComponentType<InitConfiguration>(InitConfigurationDialogComponent, componentData, new ModalSettingsTemplate('lg', 'static'));
            this.selectedTransportConfiguration.InitConfiguration = initConfigResult;
        }
        catch (err) {
            // We get here if the dialog is dismissed (canceled)
        }
    }

    async isNameAvailable(transportConfigurationName: string, transportConfigurationsId?: number): Promise<boolean> {
        try {
            return await this.transportService.isTransportInConfigurationNameAvailable(transportConfigurationName, transportConfigurationsId);
        }
        catch {
            return false;
        }
    }

    async getAvailableNameForDuplicate(transportConfigurationName: string): Promise<string> {
        let newName = "Copy of " + transportConfigurationName;
        if (await this.isNameAvailable(newName)) {
            return newName;
        }
        let nameCounter = 1;
        let nameAvailable = false;
        while (!nameAvailable && nameCounter < 100) { // Avoid infinite loop, number should never be reached.
            let tempName = newName + " (" + nameCounter + ")";
            if (await this.isNameAvailable(tempName)) {
                newName = tempName;
                nameAvailable = true;
            }
            nameCounter++;
        }
        return newName;
    }

    getSelectedTransportTypeArtifactId(): string | null {
        const transportTypeId = +this.fcTransportTypeId.value;
        const artifactId = this.transportTypes.find(e => e.Id === transportTypeId)?.ArtifactId;
        return artifactId;
    }

    updateTransportTypeHasEmbeddedMonitor(): void {
        this.currentTransportTypeHasEmbeddedMonitor.next(false);

        const artifactId = this.getSelectedTransportTypeArtifactId();
        if (artifactId) {
            this.monitorService.getHasEmbeddedMonitorType(artifactId).subscribe(res => {
                this.currentTransportTypeHasEmbeddedMonitor.next(res);
                if (!res) {
                    this.updateMonitorItem(null);
                }
            });
        }
    }

    get selectedTransportConfigurationHasMonitor(): boolean {
        const result = !!this.selectedTransportConfiguration?.MonitorItem;
        return result;
    }

    addMonitor(): void {
        const monitorItem = new MonitorItemClass({
            name: this.fcName.value || '',
            embeddedTypeArea: ArtifactAssemblyTypes.TypeTransportRetrieve,
            embeddedTypeId: this.selectedTransportConfiguration?.Id ?? 0,
        });

        this.updateMonitorItem(monitorItem);
    }

    removeMonitor(): void {
        this.updateMonitorItem(null);
    }

    get InViewMode(): boolean {
        return this.mode === Mode.View;
    }

    get InEditMode(): boolean {
        return this.mode === Mode.Edit;
    }

    get InNewMode(): boolean {
        return this.mode === Mode.New;
    }

    buildFormModel(): UntypedFormGroup {
        return this.fb.group({
            enabled: true,
            name: ['', {
                validators: [Validators.required],
                asyncValidators: [nameValidator(this.transportService, () => this.selectedTransportConfiguration.Id)],
                updateOn: 'blur'
            }],
            transportTypeId: [null, Validators.required],
            frequency: ['', [Validators.required, Validators.min(30)]],
            dataFieldsForm: this.dataFieldsFormGroup,
            monitorItem: [null],
        });
    }

    updateFormModel(): void {
        this.formModel.patchValue({
            enabled: this.selectedTransportConfiguration?.Schedule?.Enabled ?? false,
            name: this.selectedTransportConfiguration?.Name ?? '',
            transportTypeId: this.selectedTransportConfiguration?.TransportTypeId,
            frequency: this.selectedTransportConfiguration?.FrequencyInSeconds ?? '0',
        });

        this.updateMonitorItem(this.selectedTransportConfiguration?.MonitorItem);
    }

    updateMonitorItem(monitorItem: MonitorItem): void {
        this.fcMonitorItem.setValue(monitorItem);
        if (monitorItem && this.formModel.enabled) {
            this.fcMonitorItem.enable();
        }
        else {
            this.fcMonitorItem.disable();
        }

        if (this.selectedTransportConfiguration) {
            this.selectedTransportConfiguration.MonitorItem = monitorItem;
        }
    }

    get fcEnabled(): UntypedFormControl {
        return this.formModel.get('enabled') as UntypedFormControl;
    }

    get fcName(): UntypedFormControl {
        return this.formModel.get('name') as UntypedFormControl;
    }

    get fcTransportTypeId(): UntypedFormControl {
        return this.formModel.get('transportTypeId') as UntypedFormControl;
    }

    get fcFrequency(): UntypedFormControl {
        return this.formModel.get('frequency') as UntypedFormControl;
    }

    get fcMonitorItem(): UntypedFormControl {
        return this.formModel.get('monitorItem') as UntypedFormControl;
    }

    navigateToList(): Promise<boolean> {
        return this.router.navigate(['transport-locations/incoming']);
    }

    navigateToDetails(transportConfigurationsIds: number[]): Promise<boolean> {
        return this.router.navigate(['transport-locations/incoming/details', { transportConfigurationsIds }]);
    }

    navigateToLog(transportConfigurationsId: number): Promise<boolean> {
        return this.router.navigate(['transport-locations/incoming/log', { transportConfigurationsId }]);
    }

    //
    // UPLOAD TEST FILE
    //
    async uploadTestFile() {
        if (this.selectedTransportConfiguration && this.selectedTransportConfiguration.Id) {
            await this.trackingService.uploadDocument(this.selectedTransportConfiguration.Name + ' - Manual upload mode, Changes will not be saved to location', this.selectedTransportConfiguration.InitConfiguration);
        }
    }
}
